import { motion } from "motion/react";
import { REGISTER_URL } from "../constants";
import {
	Route,
	Layers,
	Layout,
	Users,
	Calendar,
	BookOpen,
	FolderOpen,
	Hotel,
	ArrowRight,
	Sparkles,
	ChevronRight,
	Clock,
} from "lucide-react";
import { Link } from "react-router-dom";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { FeaturesFlow } from "../components/features-flow/FeaturesFlow";

const features = [
	{
		icon: Route,
		label: "Route Builder",
		description:
			"Drag-and-drop routes with live map previews. Visualise every stop, transfer and destination in one seamless map view.",
		highlight: "Live map preview",
	},
	{
		icon: Layers,
		label: "Day Planner",
		description: "Plan each day hour-by-hour with flexible time blocks. Add activities, transfers and notes, everything in perfect order.",
		highlight: "Hour-by-hour planning",
	},
	{
		icon: Layout,
		label: "Templates",
		description: "Start any itinerary in seconds with gorgeous pre-built templates. Customise to match your style and save your own.",
		highlight: "100+ ready templates",
	},
	{
		icon: Users,
		label: "Collaboration",
		description: "Invite team members to co-edit any trip in real time. Comment, suggest and approve, all in one place.",
		highlight: "Real-time co-editing",
	},
	{
		icon: Calendar,
		label: "Calendar",
		description: "Visualise every trip day-by-day in a clean calendar layout. Spot overlaps, gaps and pacing issues at a glance.",
		highlight: "Visual day-by-day view",
	},
	{
		icon: BookOpen,
		label: "Content Library",
		description: "Save and reuse your best content, photos and place descriptions. Build up a rich library that speeds up every future trip.",
		highlight: "Reusable content blocks",
	},
];

const comingSoon = [
	{
		icon: FolderOpen,
		label: "Trip Documents",
		description: "Store and share all your trip documents in one place: visas, tickets and vouchers.",
	},
	{
		icon: Hotel,
		label: "Bookings",
		description: "Search and book hotels for your clients directly within Stippl. No more switching between tools.",
	},
];

const stats = [
	{ value: "3×", label: "Faster itinerary creation" },
	{ value: "98%", label: "Client satisfaction rate" },
	{ value: "40+", label: "Hours saved per month" },
];

export function TripBuildingPage() {
	return (
		<div className="min-h-screen" style={{ backgroundColor: "#0A0E27" }}>
			{/* Breadcrumb */}
			<div className="border-b border-white/5">
				<div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2">
					<Link to="/" className="text-xs text-white/40 hover:text-white/70 transition-colors">
						Home
					</Link>
					<ChevronRight className="w-3 h-3 text-white/20" />
					<span className="text-xs text-white/40">Product</span>
					<ChevronRight className="w-3 h-3 text-white/20" />
					<span className="text-xs" style={{ color: "#7C3AED" }}>
						Trip Building
					</span>
				</div>
			</div>

			{/* Hero */}
			<section className="relative overflow-hidden pt-20 pb-24 px-6">
				<div
					className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
					style={{ background: "radial-gradient(ellipse, rgba(124,58,237,0.6) 0%, transparent 70%)" }}
				/>
				<div className="max-w-7xl mx-auto relative">
					<div className="max-w-3xl">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
							className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 border"
							style={{ background: "rgba(124,58,237,0.15)", borderColor: "rgba(124,58,237,0.35)" }}
						>
							<Route className="w-3.5 h-3.5" style={{ color: "#A78BFA" }} />
							<span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#A78BFA" }}>
								Trip Building
							</span>
						</motion.div>

						<motion.h1
							initial={{ opacity: 0, y: 24 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.55, delay: 0.08 }}
							className="text-5xl md:text-6xl font-bold leading-tight mb-6"
							style={{ fontFamily: "Poppins, sans-serif", color: "#ffffff" }}
						>
							Build unforgettable<br />
							<span
								style={{
									background: "linear-gradient(90deg, #A78BFA, #7C3AED)",
									WebkitBackgroundClip: "text",
									WebkitTextFillColor: "transparent",
								}}
							>
								trips in minutes.
							</span>
						</motion.h1>

						<motion.p
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.55, delay: 0.15 }}
							className="text-lg mb-10 max-w-xl"
							style={{ color: "rgba(255,255,255,0.6)" }}
						>
							Everything you need to plan, structure and present stunning itineraries, from drag-and-drop routes to hour-by-hour day planners.
						</motion.p>

						<motion.div
							initial={{ opacity: 0, y: 16 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.22 }}
							className="flex flex-wrap items-center gap-4"
						>
							<a
								href={REGISTER_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105"
								style={{ background: "linear-gradient(135deg, #7C3AED, #6D28D9)", boxShadow: "0 0 24px rgba(124,58,237,0.45)" }}
							>
								Start building free <ArrowRight className="w-4 h-4" />
							</a>
							<a
								href="#features"
								className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm border transition-all hover:border-white/30"
								style={{ color: "rgba(255,255,255,0.8)", borderColor: "rgba(255,255,255,0.15)" }}
							>
								Explore features
							</a>
						</motion.div>
					</div>

					{/* Stats */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.35 }}
						className="mt-16 flex flex-wrap gap-8 border-t pt-10"
						style={{ borderColor: "rgba(255,255,255,0.07)" }}
					>
						{stats.map((s) => (
							<div key={s.label} className="flex flex-col gap-1">
								<span
									className="text-3xl font-bold"
									style={{
										background: "linear-gradient(90deg, #A78BFA, #7C3AED)",
										WebkitBackgroundClip: "text",
										WebkitTextFillColor: "transparent",
										fontFamily: "Poppins, sans-serif",
									}}
								>
									{s.value}
								</span>
								<span className="text-sm" style={{ color: "rgba(255,255,255,0.45)" }}>
									{s.label}
								</span>
							</div>
						))}
					</motion.div>
				</div>
			</section>

			{/* Hero image */}
			<section className="px-6 pb-20">
				<div className="max-w-7xl mx-auto">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.7, delay: 0.3 }}
						className="rounded-2xl overflow-hidden border"
						style={{ borderColor: "rgba(124,58,237,0.2)" }}
					>
						<ImageWithFallback
							src="https://images.unsplash.com/photo-1758928807778-3e09d91b83b9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0cmF2ZWwlMjBpdGluZXJhcnklMjBwbGFubmluZyUyMG1hcCUyMHJvdXRlfGVufDF8fHx8MTc3NDQzODQ4Nnww&ixlib=rb-4.1.0&q=80&w=1080"
							alt="Trip planning map view"
							className="w-full object-cover"
							style={{ height: "420px" }}
						/>
					</motion.div>
				</div>
			</section>

			{/* Features grid */}
			<section id="features" className="px-6 pb-24">
				<div className="max-w-7xl mx-auto">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6 }}
						className="mb-14"
					>
						<p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: "#7C3AED" }}>
							What's included
						</p>
						<h2
							className="text-3xl md:text-4xl font-bold mb-4"
							style={{ fontFamily: "Poppins, sans-serif", color: "#ffffff" }}
						>
							Every tool for building a perfect trip
						</h2>
						<p className="text-base max-w-xl" style={{ color: "rgba(255,255,255,0.5)" }}>
							Six powerful features working together to help you create, refine and deliver world-class itineraries.
						</p>
					</motion.div>

					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
						{features.map((feat, i) => {
							const Icon = feat.icon;
							return (
								<motion.div
									key={feat.label}
									initial={{ opacity: 0, y: 24 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true }}
									transition={{ duration: 0.5, delay: i * 0.07 }}
									whileHover={{ y: -4 }}
									className="rounded-2xl p-6 border cursor-pointer transition-all"
									style={{ background: "rgba(124,58,237,0.05)", borderColor: "rgba(124,58,237,0.15)" }}
									onMouseEnter={(e) => {
										(e.currentTarget as HTMLDivElement).style.borderColor = "rgba(124,58,237,0.4)";
										(e.currentTarget as HTMLDivElement).style.background = "rgba(124,58,237,0.1)";
									}}
									onMouseLeave={(e) => {
										(e.currentTarget as HTMLDivElement).style.borderColor = "rgba(124,58,237,0.15)";
										(e.currentTarget as HTMLDivElement).style.background = "rgba(124,58,237,0.05)";
									}}
								>
									<div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(124,58,237,0.2)" }}>
										<Icon className="w-5 h-5" style={{ color: "#A78BFA" }} />
									</div>
									<div
										className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-3 text-[10px] font-semibold"
										style={{ background: "rgba(124,58,237,0.2)", color: "#C4B5FD" }}
									>
										<Sparkles className="w-2.5 h-2.5" />
										{feat.highlight}
									</div>
									<h3 className="font-bold text-base mb-2" style={{ color: "#ffffff", fontFamily: "Poppins, sans-serif" }}>
										{feat.label}
									</h3>
									<p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
										{feat.description}
									</p>
								</motion.div>
							);
						})}
					</div>
				</div>
			</section>

			{/* Scrolling workflow demo */}
			<FeaturesFlow />

			{/* Coming soon */}
			<section className="px-6 pb-24">
				<div className="max-w-7xl mx-auto">
					<div
						className="rounded-2xl p-8 md:p-12 border"
						style={{
							background: "linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(10,14,39,0.8) 100%)",
							borderColor: "rgba(124,58,237,0.2)",
						}}
					>
						<div className="flex items-center gap-2 mb-2">
							<Clock className="w-4 h-4" style={{ color: "#A78BFA" }} />
							<span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#A78BFA" }}>
								Coming soon
							</span>
						</div>
						<h3 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "Poppins, sans-serif", color: "#ffffff" }}>
							Even more on the way
						</h3>
						<p className="text-sm mb-8" style={{ color: "rgba(255,255,255,0.45)" }}>
							We're building these features right now, sign up to get early access.
						</p>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
							{comingSoon.map((feat) => {
								const Icon = feat.icon;
								return (
									<div
										key={feat.label}
										className="flex items-start gap-4 p-5 rounded-xl border"
										style={{ background: "rgba(124,58,237,0.07)", borderColor: "rgba(124,58,237,0.15)" }}
									>
										<div
											className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
											style={{ background: "rgba(124,58,237,0.2)" }}
										>
											<Icon className="w-4 h-4" style={{ color: "#A78BFA" }} />
										</div>
										<div>
											<div className="flex items-center gap-2 mb-1">
												<h4 className="font-semibold text-sm" style={{ color: "#ffffff" }}>
													{feat.label}
												</h4>
												<span
													className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
													style={{ background: "rgba(124,58,237,0.3)", color: "#C4B5FD" }}
												>
													Soon
												</span>
											</div>
											<p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.45)" }}>
												{feat.description}
											</p>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</section>

			{/* CTA */}
			<section className="px-6 pb-24">
				<div className="max-w-7xl mx-auto">
					<motion.div
						initial={{ opacity: 0, y: 24 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6 }}
						className="text-center rounded-3xl px-8 py-16 border relative overflow-hidden"
						style={{
							background: "linear-gradient(135deg, rgba(124,58,237,0.2) 0%, rgba(10,14,39,0.9) 60%)",
							borderColor: "rgba(124,58,237,0.25)",
						}}
					>
						<div
							className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-40 rounded-full blur-3xl opacity-30 pointer-events-none"
							style={{ background: "rgba(124,58,237,0.8)" }}
						/>
						<div className="relative">
							<h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "Poppins, sans-serif", color: "#ffffff" }}>
								Ready to build better trips?
							</h2>
							<p className="text-base mb-8 max-w-md mx-auto" style={{ color: "rgba(255,255,255,0.5)" }}>
								Join travel professionals already using Stippl to create itineraries their clients love.
							</p>
							<a
								href={REGISTER_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105"
								style={{ background: "linear-gradient(135deg, #7C3AED, #6D28D9)", boxShadow: "0 0 30px rgba(124,58,237,0.5)" }}
							>
								Start for free <ArrowRight className="w-4 h-4" />
							</a>
						</div>
					</motion.div>
				</div>
			</section>
		</div>
	);
}
